/**
 * FontPreparer - Utility for preparing fonts with bytes for WASM initialization
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility handles font preloading and format conversion for WASM document initialization.
 * It downloads font bytes and converts them to the format expected by the WASM typesetter.
 */

import { FontDownloadService } from '../../../../wordprocessing/rendering/fonts/FontDownloadService';

/**
 * Input font format (from initializeDocument options)
 */
export interface FontInput {
  fontUid: string;
  family: string;
  weight: number;
  style: string;
}

/**
 * Output font format (for WASM initialization)
 */
export interface FontWithBytes {
  fontUid: string;
  family: string;
  weight: number;
  style: string;
  bytes: number[]; // Array of bytes for WASM
}

/**
 * Prepare fonts with bytes for WASM document initialization
 * 
 * This function:
 * 1. Preloads font bytes using FontDownloadService
 * 2. Converts font bytes to number[] format for WASM
 * 3. Merges font metadata (family, weight, style) with downloaded bytes
 * 4. Handles errors gracefully with fallback to metadata-only fonts
 * 
 * @param fonts - Array of font inputs with UID, family, weight, and style
 * @returns Promise resolving to array of fonts with bytes in WASM format
 */
export async function prepareFontsWithBytes(fonts: FontInput[]): Promise<FontWithBytes[]> {
  try {
    // Preload font bytes from the download service
    const preloaded = await FontDownloadService.preloadFonts(
      fonts.map(f => ({ font_uid: f.fontUid, fileId: f.fontUid }))
    );
    
    // Map preloaded fonts to WASM format, merging with original font metadata
    return preloaded.map(pl => {
      // Create a copy of the bytes array to avoid issues with detached ArrayBuffers
      const copy = new Uint8Array(pl.bytes.length);
      copy.set(pl.bytes);
      
      // Find the original font metadata to preserve family, weight, and style
      const originalFont = fonts.find(x => x.fontUid === pl.font_uid);
      
      return {
        fontUid: pl.font_uid,
        family: originalFont?.family || pl.font_uid,
        weight: originalFont?.weight || 400,
        style: originalFont?.style || 'normal',
        bytes: Array.from(copy) as number[] // Convert to number[] for WASM
      };
    });
  } catch (error) {
    // Fallback to metadata only if preload fails
    // This allows document initialization to proceed even if fonts fail to load
    console.warn('[FontPreparer] Font preload failed, using metadata-only fallback:', error);
    return fonts.map(f => ({
      fontUid: f.fontUid,
      family: f.family,
      weight: f.weight,
      style: f.style,
      bytes: [] as number[] // Empty bytes array - WASM will need to handle this
    }));
  }
}

